import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Check, X, ArrowLeftRight, Package, Cable, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PropertyCompareView({ token, properties }) {
  const [leftProp, setLeftProp] = useState('');
  const [rightProp, setRightProp] = useState('');
  const [leftData, setLeftData] = useState(null);
  const [rightData, setRightData] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!leftProp) return;
    axios.get(`${BACKEND_URL}/api/stack?property_id=${leftProp}`, { headers }).then(r => setLeftData(r.data)).catch(() => {});
  }, [leftProp, headers]);

  useEffect(() => {
    if (!rightProp) return;
    axios.get(`${BACKEND_URL}/api/stack?property_id=${rightProp}`, { headers }).then(r => setRightData(r.data)).catch(() => {});
  }, [rightProp, headers]);

  const leftNodes = (leftData?.nodes || []).filter(n => n.used);
  const rightNodes = (rightData?.nodes || []).filter(n => n.used);
  const leftPlats = new Set(leftNodes.map(n => n.id));
  const rightPlats = new Set(rightNodes.map(n => n.id));
  const allPlats = [...new Set([...leftPlats, ...rightPlats])].sort();

  return (
    <div className="flex flex-col h-full" data-testid="property-compare-view">
      {/* Selectors */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card/50 shrink-0">
        <Select value={leftProp} onValueChange={setLeftProp}>
          <SelectTrigger className="flex-1 h-9 text-sm"><SelectValue placeholder="Select first property..." /></SelectTrigger>
          <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <ArrowLeftRight className="h-4 w-4 text-muted-foreground shrink-0" />
        <Select value={rightProp} onValueChange={setRightProp}>
          <SelectTrigger className="flex-1 h-9 text-sm"><SelectValue placeholder="Select second property..." /></SelectTrigger>
          <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Comparison Grid */}
      {leftProp && rightProp && leftData && rightData && (
        <ScrollArea className="flex-1">
          <div className="p-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-6 text-center text-xs">
              <div className="p-2 rounded-lg bg-blue-50">
                <div className="text-lg font-bold text-blue-700" style={{ fontFamily: 'Space Grotesk' }}>{leftNodes.length}</div>
                <div className="text-muted-foreground">{leftData.property?.name}</div>
              </div>
              <div className="p-2 rounded-lg bg-muted">
                <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{[...leftPlats].filter(p => rightPlats.has(p)).length}</div>
                <div className="text-muted-foreground">Shared</div>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50">
                <div className="text-lg font-bold text-emerald-700" style={{ fontFamily: 'Space Grotesk' }}>{rightNodes.length}</div>
                <div className="text-muted-foreground">{rightData.property?.name}</div>
              </div>
            </div>

            {/* Platform-by-platform comparison */}
            <div className="border rounded-xl overflow-hidden">
              <div className="grid grid-cols-3 bg-muted/40 px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b">
                <div>{leftData.property?.name}</div>
                <div className="text-center">Platform</div>
                <div className="text-right">{rightData.property?.name}</div>
              </div>
              <div className="divide-y">
                {allPlats.map(platId => {
                  const leftNode = leftNodes.find(n => n.id === platId);
                  const rightNode = rightNodes.find(n => n.id === platId);
                  const anyNode = leftNode || rightNode;
                  return (
                    <div key={platId} className="grid grid-cols-3 px-3 py-2 text-xs items-center hover:bg-muted/20">
                      <div>
                        {leftNode ? (
                          <div className="flex items-center gap-1.5">
                            <Check className="h-3 w-3 text-blue-600 shrink-0" />
                            <span className="font-medium truncate">{leftNode.product_name || leftNode.vendor_name || '—'}</span>
                          </div>
                        ) : <X className="h-3 w-3 text-muted-foreground/30" />}
                      </div>
                      <div className="text-center">
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: anyNode?.category_color + '40', color: anyNode?.category_color }}>
                          {anyNode?.short || platId}
                        </Badge>
                      </div>
                      <div className="text-right">
                        {rightNode ? (
                          <div className="flex items-center gap-1.5 justify-end">
                            <span className="font-medium truncate">{rightNode.product_name || rightNode.vendor_name || '—'}</span>
                            <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                          </div>
                        ) : <div className="flex justify-end"><X className="h-3 w-3 text-muted-foreground/30" /></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
      )}

      {(!leftProp || !rightProp) && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Select two properties to compare side by side</p>
        </div>
      )}
    </div>
  );
}
